'use client';

import * as React from 'react';
import type { CarouselApi } from '@/hen-ui/shadcn/carousel';
import { measureSlidesPerView, pageIndexAfterResize } from './carouselGroups';

type PendingRealign = {
  slideCount: number;
  previousPerView: number;
  previousPage: number;
  nextPerView: number;
};

/**
 * Measures how many slides fit and remembers which group to keep selected
 * when that number changes. Grouped paging reads the returned count.
 */
export function useGroupedSlidesPerView(
  api: CarouselApi | undefined,
  enabled: boolean,
): number {
  const [perView, setPerView] = React.useState(1);
  const pendingRef = React.useRef<PendingRealign | null>(null);

  React.useLayoutEffect(() => {
    if (!api || !enabled) return;

    const viewport = api.rootNode();
    let observer: ResizeObserver | undefined;
    const observed = new Set<Element>();

    const measure = () => {
      const slide = api.slideNodes()[0];
      if (observer && slide && !observed.has(slide)) {
        observer.observe(slide);
        observed.add(slide);
      }

      // The track is one gutter wider than the viewport. Fractional slides
      // (half, third, fifth) are sized from the track, so the viewport
      // undercounts them by one.
      const next = measureSlidesPerView(
        api.containerNode().getBoundingClientRect().width,
        slide?.getBoundingClientRect().width ?? 0,
      );
      const slideCount = api.slideNodes().length;
      const previousPage = api.selectedScrollSnap();
      setPerView((current) => {
        if (current === next) return current;
        pendingRef.current = {
          slideCount,
          previousPerView: current,
          previousPage,
          nextPerView: next,
        };
        return next;
      });
    };

    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(viewport);
      observed.add(viewport);
    }

    measure();
    api.on('slidesChanged', measure);

    return () => {
      observer?.disconnect();
      api.off('slidesChanged', measure);
    };
  }, [api, enabled]);

  React.useEffect(() => {
    if (!api || !enabled) return;
    const pending = pendingRef.current;
    if (!pending || pending.nextPerView !== perView) return;
    pendingRef.current = null;

    const page = pageIndexAfterResize(pending);
    const last = Math.max(0, api.scrollSnapList().length - 1);
    const next = Math.min(Math.max(0, page), last);
    if (api.selectedScrollSnap() !== next) {
      api.scrollTo(next, true);
    }
  }, [api, enabled, perView]);

  return enabled ? perView : 1;
}
